import React, { useEffect, useReducer, useState } from "react";
import { useHistory } from "react-router-dom";
import { CopyToClipboard } from "react-copy-to-clipboard";

import Card from "../../common/card/card";
import Button from "../../common/button/button.component";
import PrivateComponent from "../../common/private-component/private-component.component";
import { API_STATUS } from "../../constants/app.constants";
import { isLoggedInUser } from "../../utils/app.utils";
import useSelectedProduct from "../../componentHooks/useSelectedProduct";
import useFetchApiKey from "../../apiHooks/useFetchApiKey";
import useFetchUserInfo from "../../apiHooks/fetch-user-info/useFetchUserInfo";
import useGetClientCredentials from "../../apiHooks/useGetClientCredentials";
import useCustomerInformation from "../../apiHooks/useCustomerInformation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy } from "@fortawesome/fontawesome-free-solid";

import "./iot-api-key-card.component.scss";
import { MergeStateReducer } from "../../common/input/mergeStateReducer";
import useFetchUserAccountInfo from "../../apiHooks/fetch-user-info/useFetchUserAccountInfo";

const TOOLTP_INITIAL_STATE = {
  apiKeyTooltip: null,
  clientIdTooltip: null,
  clientSecretTooltip: null,
  customerIdTooltip: null,
  customerAccountNoTooltip: null,
  ivUserTooltip: null,
};

const IotApiKeyCard = () => {
  const { content: userInfo, status: userInfoStatus } = useFetchUserInfo();
  const { selectedProduct } = useSelectedProduct();
  const { fetchApiKey, content: apiKey, status: fetchApiKeyStatus } = useFetchApiKey();
  const {
    getClientCredentials,
    content: clientCredentials,
  } = useGetClientCredentials();
  const { content: customerInformation, fetchCustomerInformation, status: customerInformationStatus } =
    useCustomerInformation();
    const { fetchUserAccountInfo, content: userAccountInfo } = useFetchUserAccountInfo();
  const [tooltip, setTooltip] = useReducer(MergeStateReducer, {
    ...TOOLTP_INITIAL_STATE,
  });
  const {
    apiKeyTooltip,
    clientIdTooltip,
    clientSecretTooltip,
    customerAccountNoTooltip,
    customerIdTooltip,
    ivUserTooltip,
  } = tooltip;

  useEffect(() => {
    if (isLoggedInUser()) {
      fetchApiKey({
        product: selectedProduct.value,
        username: userInfo?.preferred_username,
      });
      getClientCredentials({ username: userInfo.preferred_username });
      fetchCustomerInformation({ emailId: userInfo.email });
      fetchUserAccountInfo();
    }
  }, [selectedProduct.value]);

  const onShowTooltip = (type) => {
    setTooltip({ [type]: "Copied" });
    setTimeout(() => setTooltip({ [type]: null }), 3000);
  };

  if (fetchApiKeyStatus != API_STATUS.SUCCESS) {
    return null;
  }

  if(userAccountInfo?.attributes?.isProspectiveUser && userAccountInfo?.attributes?.isProspectiveUser[0]){
    return null;
  }

  return (
    <PrivateComponent showAccessDenied={false}>
      <Card cardClass="api-key-card">
        <div className="info-details">
          <div className="text">
            <h3 className="heading mr-1 text-l dark:text-neutral-50">
              API Key:
            </h3>
            <p className="info leading-tight">{apiKey}</p>
            <div className="copy-button">
              <CopyToClipboard
                text={apiKey}
                onCopy={() => onShowTooltip("apiKeyTooltip")}
              >
                <FontAwesomeIcon icon={faCopy} className="fa-copy-icon" />
              </CopyToClipboard>
              {apiKeyTooltip && (
                <div
                  data-tooltip="tooltip"
                  class="absolute z-50 whitespace-normal break-words rounded-lg bg-black py-1.5 px-3 font-sans text-sm font-normal text-white focus:outline-none"
                >
                  {apiKeyTooltip}
                </div>
              )}
            </div>
          </div>

          <div className="text">
            <h3 className="heading text-l mr-2 dark:text-neutral-50">
              Client ID:
            </h3>
            <div className="flex flex-row">
              <p className="info leading-tight">{clientCredentials?.clientId}</p>

              <div className="copy-button">
                <CopyToClipboard
                  text={clientCredentials?.clientId}
                  onCopy={() => onShowTooltip("clientIdTooltip")}
                >
                  <FontAwesomeIcon icon={faCopy} className="fa-copy-icon" />
                </CopyToClipboard>
                {clientIdTooltip && (
                  <div
                    data-tooltip="tooltip"
                    class="absolute z-50 whitespace-normal break-words rounded-lg bg-black py-1.5 px-3 font-sans text-sm font-normal text-white focus:outline-none"
                  >
                    {clientIdTooltip}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="text">
            <h3 className="heading text-l mr-2 dark:text-neutral-50">
              Client Secret:
            </h3>
            <div className="flex flex-row">
              <p className="info leading-tight">
                {clientCredentials?.clientSecret}
              </p>

              <div className="copy-button">
                <CopyToClipboard
                  text={clientCredentials?.clientSecret}
                  onCopy={() => onShowTooltip("clientSecretTooltip")}
                >
                  <FontAwesomeIcon icon={faCopy} className="fa-copy-icon" />
                </CopyToClipboard>
                {clientSecretTooltip && (
                  <div
                    data-tooltip="tooltip"
                    class="absolute z-50 whitespace-normal break-words rounded-lg bg-black py-1.5 px-3 font-sans text-sm font-normal text-white focus:outline-none"
                  >
                    {clientSecretTooltip}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="text">
            <h3 className="heading text-l mr-2 dark:text-neutral-50">
              Customer Id:
            </h3>
            <div className="flex flex-row">
              <p className="info leading-tight">
                {customerInformation?.customerId}
              </p>

              <div className="copy-button">
                <CopyToClipboard
                  text={customerInformation?.customerId}
                  onCopy={() => onShowTooltip("customerIdTooltip")}
                >
                  <FontAwesomeIcon icon={faCopy} className="fa-copy-icon" />
                </CopyToClipboard>
                {customerIdTooltip && (
                  <div
                    data-tooltip="tooltip"
                    class="absolute z-50 whitespace-normal break-words rounded-lg bg-black py-1.5 px-3 font-sans text-sm font-normal text-white focus:outline-none"
                  >
                    {customerIdTooltip}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="text">
            <h3 className="heading text-l mr-2 dark:text-neutral-50">
              Customer Account Number:
            </h3>
            <div className="flex flex-row">
              <p className="info leading-tight">
                {customerInformation?.customerAccountNo}
              </p>

              <div className="copy-button">
                <CopyToClipboard
                  text={customerInformation?.customerAccountNo}
                  onCopy={() => onShowTooltip("customerAccountNoTooltip")}
                >
                  <FontAwesomeIcon icon={faCopy} className="fa-copy-icon" />
                </CopyToClipboard>
                {customerAccountNoTooltip && (
                  <div
                    data-tooltip="tooltip"
                    class="absolute z-50 whitespace-normal break-words rounded-lg bg-black py-1.5 px-3 font-sans text-sm font-normal text-white focus:outline-none"
                  >
                    {customerAccountNoTooltip}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="text">
            <h3 className="heading text-l mr-2 dark:text-neutral-50">
              iv-user:
            </h3>
            <div className="flex flex-row">
              <p className="info leading-tight">
                {customerInformation?.ivUser}
              </p>

              <div className="copy-button">
                <CopyToClipboard
                  text={customerInformation?.ivUser}
                  onCopy={() => onShowTooltip("ivUserTooltip")}
                >
                  <FontAwesomeIcon icon={faCopy} className="fa-copy-icon" />
                </CopyToClipboard>
                {ivUserTooltip && (
                  <div
                    data-tooltip="tooltip"
                    class="absolute z-50 whitespace-normal break-words rounded-lg bg-black py-1.5 px-3 font-sans text-sm font-normal text-white focus:outline-none"
                  >
                    {ivUserTooltip}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Card>
    </PrivateComponent>
  );
};

export default IotApiKeyCard;
